import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverAnchor,
  Portal,
  Spinner,
  Text,
  Textarea,
  VStack,
  useColorModeValue
} from "@chakra-ui/react";

const asArray = (v) => (Array.isArray(v) ? v : []);

const getCommentText = (c) => {
  if (!c) return "";
  return (
    c.comment ??
    c.text ??
    c.content ??
    c.body ??
    c.message ??
    ""
  );
};

const getCreatedAt = (c) => {
  if (!c) return null;
  return c.created_at ?? c.createdAt ?? c.created ?? c.inserted_at ?? c.date ?? null;
};

const getAuthorLabel = (c) => {
  const a = c?.author ?? c?.user ?? c?.createdBy ?? c?.created_by ?? null;
  const first = a?.first_name ?? a?.firstName ?? "";
  const last = a?.last_name ?? a?.lastName ?? "";
  const email = a?.email ?? "";
  const full = `${first} ${last}`.trim();
  return full || email || c?.authorName || c?.author_email || "";
};

const getAuthorAvatarUrl = (c) => {
  const a = c?.author ?? c?.user ?? c?.createdBy ?? c?.created_by ?? null;
  return a?.avatar_url ?? a?.avatarUrl ?? a?.avatar ?? null;
};

const getReplies = (c) => {
  if (!c) return [];
  return (
    c.replies ??
    c.children ??
    c.nested ??
    c.thread ??
    c.comments ??
    []
  );
};

const normalizeTree = (raw) => {
  const input = asArray(raw?.items ?? raw?.data ?? raw);
  return input.map((c) => ({
    raw: c,
    id: c?.id ?? c?._id ?? `${getCreatedAt(c) || ""}-${getCommentText(c) || ""}`,
    text: getCommentText(c),
    createdAt: getCreatedAt(c),
    authorLabel: getAuthorLabel(c),
    authorAvatarUrl: getAuthorAvatarUrl(c),
    replies: normalizeTree(getReplies(c))
  }));
};

const formatDateTime = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return "";
  }
};

// Render text with @mentions highlighted
const MentionText = ({ text }) => {
  const mentionColor = useColorModeValue("blue.600", "blue.300");
  if (!text) return <Text fontSize="sm">—</Text>;

  const parts = text.split(/(@[\w.+-]+)/g);
  return (
    <Text fontSize="sm" whiteSpace="pre-wrap">
      {parts.map((part, i) =>
        /^@[\w.+-]+$/.test(part) ? (
          <Text as="span" key={i} color={mentionColor} fontWeight="semibold">
            {part}
          </Text>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </Text>
  );
};

// Mention-aware textarea with dropdown
const MentionTextarea = ({ value, onChange, placeholder, rows, mentionableUsers }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef(null);
  const popoverBg = useColorModeValue("white", "gray.700");
  const hoverBg = useColorModeValue("gray.100", "gray.600");

  const filterUsers = useCallback(
    (query) => {
      if (!mentionableUsers?.length) return [];
      const q = query.toLowerCase();
      return mentionableUsers
        .filter((u) => {
          const full = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
          const dotName = `${(u.first_name || "").toLowerCase()}.${(u.last_name || "").toLowerCase()}`;
          return full.includes(q) || dotName.includes(q) || (u.email || "").toLowerCase().includes(q);
        })
        .slice(0, 8);
    },
    [mentionableUsers]
  );

  const handleChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    onChange(newValue);

    // Check if we're in a mention context
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf("@");

    if (atIndex >= 0) {
      const charBeforeAt = atIndex > 0 ? textBeforeCursor[atIndex - 1] : " ";
      // Only trigger if @ is at start or preceded by whitespace
      if (atIndex === 0 || /\s/.test(charBeforeAt)) {
        const query = textBeforeCursor.slice(atIndex + 1);
        // No spaces allowed in mention query
        if (!/\s/.test(query)) {
          const filtered = filterUsers(query);
          setMentionQuery(query);
          setMentionStartIndex(atIndex);
          setSuggestions(filtered);
          setShowSuggestions(filtered.length > 0);
          setSelectedIndex(0);
          return;
        }
      }
    }

    setShowSuggestions(false);
  };

  const insertMention = useCallback(
    (user) => {
      const mention = `${(user.first_name || "").toLowerCase()}.${(user.last_name || "").toLowerCase()}`;
      const before = value.slice(0, mentionStartIndex);
      const after = value.slice(mentionStartIndex + 1 + mentionQuery.length);
      const newValue = `${before}@${mention} ${after}`;
      onChange(newValue);
      setShowSuggestions(false);

      // Restore focus
      setTimeout(() => {
        if (textareaRef.current) {
          const pos = mentionStartIndex + mention.length + 2; // @mention + space
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(pos, pos);
        }
      }, 0);
    },
    [value, mentionStartIndex, mentionQuery, onChange]
  );

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && suggestions[selectedIndex]) {
      e.preventDefault();
      insertMention(suggestions[selectedIndex]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  return (
    <Popover isOpen={showSuggestions} autoFocus={false} placement="bottom-start" matchWidth>
      <PopoverAnchor>
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Small delay to allow click on suggestion
            setTimeout(() => setShowSuggestions(false), 200);
          }}
          rows={rows}
          placeholder={placeholder}
        />
      </PopoverAnchor>
      <Portal>
        <PopoverContent
          bg={popoverBg}
          shadow="lg"
          borderRadius="md"
          maxH="200px"
          overflowY="auto"
          w="auto"
          minW="200px"
        >
          <PopoverBody p={1}>
            <List spacing={0}>
              {suggestions.map((user, i) => (
                <ListItem
                  key={user.id}
                  px={3}
                  py={2}
                  cursor="pointer"
                  bg={i === selectedIndex ? hoverBg : "transparent"}
                  borderRadius="md"
                  _hover={{ bg: hoverBg }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertMention(user);
                  }}
                >
                  <HStack spacing={2}>
                    <Avatar size="xs" name={`${user.first_name || ""} ${user.last_name || ""}`} />
                    <Box>
                      <Text fontSize="sm" fontWeight="medium">
                        {user.first_name} {user.last_name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {user.email}
                      </Text>
                    </Box>
                  </HStack>
                </ListItem>
              ))}
            </List>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const CommentNode = ({
  node,
  depth,
  replyLabel,
  cancelLabel,
  replyPlaceholder,
  submitLabel,
  onCreate,
  onReload,
  onCreateSuccess,
  onCreateError,
  mentionableUsers
}) => {
  const [isReplying, setIsReplying] = useState(false);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const bubbleBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const bubbleBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const threadBorder = useColorModeValue("gray.100", "whiteAlpha.200");

  const handleSubmitReply = async () => {
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      await onCreate?.({ text, parentCommentId: node.id });
      setDraft("");
      setIsReplying(false);
      await onReload?.();
      onCreateSuccess?.();
    } catch (e) {
      onCreateError?.(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Flex align="flex-start" gap={3}>
        <Avatar
          size="sm"
          name={node.authorLabel || "—"}
          src={node.authorAvatarUrl || undefined}
        />

        <Box flex="1" minW={0}>
          <HStack justify="space-between" align="baseline" spacing={3} mb={1}>
            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
              {node.authorLabel || "—"}
            </Text>
            <Text fontSize="xs" color="gray.500" flexShrink={0}>
              {node.createdAt ? formatDateTime(node.createdAt) : ""}
            </Text>
          </HStack>

          <Box
            bg={bubbleBg}
            borderWidth="1px"
            borderColor={bubbleBorder}
            borderRadius="lg"
            px={3}
            py={2}
          >
            <MentionText text={node.text} />
          </Box>

          <HStack mt={2} spacing={3}>
            <Button
              variant="link"
              size="xs"
              colorScheme="purple"
              onClick={() => setIsReplying((v) => !v)}
            >
              {replyLabel}
            </Button>
          </HStack>

          {isReplying && (
            <Box mt={2}>
              <MentionTextarea
                value={draft}
                onChange={setDraft}
                rows={2}
                placeholder={replyPlaceholder}
                mentionableUsers={mentionableUsers}
              />
              <HStack mt={2} spacing={2}>
                <Button
                  size="xs"
                  colorScheme="teal"
                  onClick={handleSubmitReply}
                  isDisabled={!draft.trim()}
                  isLoading={submitting}
                >
                  {submitLabel}
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    setDraft("");
                    setIsReplying(false);
                  }}
                  isDisabled={submitting}
                >
                  {cancelLabel}
                </Button>
              </HStack>
            </Box>
          )}

          {node.replies?.length ? (
            <VStack
              align="stretch"
              spacing={4}
              mt={4}
              pl={6}
              borderLeft="2px solid"
              borderLeftColor={threadBorder}
            >
              {node.replies.map((r) => (
                <CommentNode
                  key={r.id}
                  node={r}
                  depth={depth + 1}
                  replyLabel={replyLabel}
                  cancelLabel={cancelLabel}
                  replyPlaceholder={replyPlaceholder}
                  submitLabel={submitLabel}
                  onCreate={onCreate}
                  onReload={onReload}
                  onCreateSuccess={onCreateSuccess}
                  onCreateError={onCreateError}
                  mentionableUsers={mentionableUsers}
                />
              ))}
            </VStack>
          ) : null}
        </Box>
      </Flex>
    </Box>
  );
};

const ThreadedComments = ({
  title,
  comments,
  isLoading,
  emptyLabel,
  submitLabel,
  replyLabel,
  cancelLabel,
  placeholder,
  replyPlaceholder,
  onCreate,
  onReload,
  onCreateSuccess,
  onCreateError,
  mentionableUsers
}) => {
  const normalized = useMemo(() => normalizeTree(comments), [comments]);
  const [newComment, setNewComment] = useState("");
  const [submittingRoot, setSubmittingRoot] = useState(false);
  const composerBorder = useColorModeValue("gray.200", "whiteAlpha.200");

  const handleSubmitRoot = async () => {
    const text = newComment.trim();
    if (!text) return;
    setSubmittingRoot(true);
    try {
      await onCreate?.({ text, parentCommentId: undefined });
      setNewComment("");
      await onReload?.();
      onCreateSuccess?.();
    } catch (e) {
      onCreateError?.(e);
    } finally {
      setSubmittingRoot(false);
    }
  };

  return (
    <Box>
      {title ? (
        <Text fontSize="xl" fontWeight="semibold" mb={2}>
          {title}
        </Text>
      ) : null}

      <VStack align="stretch" spacing={4} mb={4}>
        {isLoading ? (
          <Center py={2}>
            <Spinner size="sm" />
          </Center>
        ) : normalized.length === 0 ? (
          <Text fontSize="sm" color="gray.500">
            {emptyLabel}
          </Text>
        ) : (
          normalized.map((node) => (
            <CommentNode
              key={node.id}
              node={node}
              depth={0}
              replyLabel={replyLabel}
              cancelLabel={cancelLabel}
              replyPlaceholder={replyPlaceholder}
              submitLabel={submitLabel}
              onCreate={onCreate}
              onReload={onReload}
              onCreateSuccess={onCreateSuccess}
              onCreateError={onCreateError}
              mentionableUsers={mentionableUsers}
            />
          ))
        )}
      </VStack>

      <Box
        borderWidth="1px"
        borderColor={composerBorder}
        borderRadius="lg"
        p={3}
      >
        <MentionTextarea
          value={newComment}
          onChange={setNewComment}
          rows={3}
          placeholder={placeholder}
          mentionableUsers={mentionableUsers}
        />
        <HStack mt={2} justify="flex-end">
          <Button
            size="sm"
            colorScheme="teal"
            onClick={handleSubmitRoot}
            isDisabled={!newComment.trim()}
            isLoading={submittingRoot}
          >
            {submitLabel}
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default ThreadedComments;
